/**
 * Telegram Platform
 * 
 * Интеграция с Telegram WebApp API
 */

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

class TelegramPlatform {
  private user: TelegramUser | null = null;
  private initialized = false;
  
  private getTg() {
    return (window as any).Telegram?.WebApp;
  }
  
  /**
   * Инициализация Telegram WebApp
   */
  async init(): Promise<void> {
    if (this.initialized) return;
    
    const tg = this.getTg();
    if (!tg) {
      console.warn('[TG] Telegram WebApp not available');
      return;
    }
    
    // Получаем данные пользователя
    const userData = tg.initDataUnsafe?.user;
    if (userData) {
      this.user = {
        id: userData.id,
        first_name: userData.first_name,
        last_name: userData.last_name,
        username: userData.username,
        language_code: userData.language_code,
      };
    }
    
    // Говорим Telegram что приложение готово
    tg.ready?.();
    tg.expand?.();
    
    this.initialized = true;
    console.log('[TG] Initialized, user:', this.user?.first_name, this.user?.id);
  }
  
  /**
   * Получить данные пользователя
   */
  getUser(): TelegramUser | null {
    return this.user;
  }
  
  /**
   * ID пользователя
   */
  getUserId(): number | null {
    return this.user?.id || null;
  }
  
  /**
   * Имя пользователя
   */
  getDisplayName(): string {
    if (!this.user) return 'Гость';
    return [this.user.first_name, this.user.last_name].filter(Boolean).join(' ');
  }
  
  /**
   * Аватар (Telegram не даёт напрямую)
   */
  getAvatar(): string | null {
    return null;
  }
  
  /**
   * Хаптик-фидбек
   */
  haptic(type: 'impact' | 'notification' | 'selection' = 'selection'): void {
    const tg = this.getTg();
    if (!tg?.HapticFeedback) return;
    
    try {
      if (type === 'impact') {
        tg.HapticFeedback.impactOccurred?.('light');
      } else if (type === 'notification') {
        tg.HapticFeedback.notificationOccurred?.('success');
      } else {
        tg.HapticFeedback.selectionChanged?.();
      }
    } catch {
      // Игнорируем
    }
  }
  
  /**
   * Поделиться (открывает Telegram share)
   */
  share(text: string, url?: string): void {
    const tg = this.getTg();
    const shareUrl = url 
      ? `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`
      : `https://t.me/share/url?text=${encodeURIComponent(text)}`;
    
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(shareUrl);
    } else {
      window.open(shareUrl, '_blank');
    }
  }
  
  /**
   * Показать алерт
   */
  showAlert(message: string): void {
    const tg = this.getTg();
    if (tg?.showAlert) {
      tg.showAlert(message);
    } else {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message } }));
    }
  }
  
  /**
   * Закрыть приложение
   */
  close(): void {
    const tg = this.getTg();
    tg?.close?.();
  }
  
  /**
   * Получить initData для подписи
   */
  getInitData(): string {
    return this.getTg()?.initData || '';
  }
}

// Singleton
export const telegramPlatform = new TelegramPlatform();